import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useParkingStore } from '@illegally-parked/store/store';
import { MapTemplate } from '@templates';
import { ErrorMessages, AssetType } from '@types';
import {
  addressResponseType,
  convertAddressToServiceLocation,
  convertServiceLocationToAddress,
  parseZodErrors,
  scrollTop,
  TextAreaMaxLengthSchema,
} from '@utils';
import { SelectedLocationTextContent } from './SelectedLocationTextContent';
import { LatLngTuple } from 'leaflet';
import { ZodError } from 'zod';
import { RoutesList } from '@illegally-parked/routes/Routes';
import { additionalLocationDetails } from '../../../../constants/questionData';

export const IssueLocation = () => {
  const navigate = useNavigate();

  const {
    updateServiceLocationDetails,
    serviceLocationDetails,
    isPhotoLocationStored,
    issueLocationMoreInfo,
    updateIssuelocationMoreInfo,
  } = useParkingStore();

  const { longitude, latitude } = serviceLocationDetails;

  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);

  const selectedAddress =
    latitude && longitude
      ? convertServiceLocationToAddress(serviceLocationDetails)
      : ({} as addressResponseType);
  const defaultLocation =
    selectedAddress?.latitude && selectedAddress?.longitude
      ? ([
          parseFloat(selectedAddress.latitude),
          parseFloat(selectedAddress.longitude),
        ] as LatLngTuple)
      : undefined;

  const errorPath = 'issue-Location';
  const assetType: AssetType = 'HighwaysCarriageways';

  const navigationIfInsideTfLBoundary = () => {
    setErrorMessages([]);
    navigate(RoutesList.REPORT_TO_TFL);
    scrollTop();
  };

  const handleOnNextClick = async () => {
    setErrorMessages([]);
    try {
      const { latitude, longitude } = serviceLocationDetails;
      if (!latitude || !longitude) {
        setErrorMessages([
          {
            message: 'Please select a location on the map',
            path: errorPath,
          },
        ]);
        return;
      }

      TextAreaMaxLengthSchema({
        fieldNames: [additionalLocationDetails.id],
        maxLength: additionalLocationDetails.maxLength,
      }).parse({
        [additionalLocationDetails.id]: issueLocationMoreInfo,
      });

      navigate(RoutesList.DATETIME_DETAILS);
    } catch (error) {
      const validationErrors = parseZodErrors(error as ZodError);
      setErrorMessages(
        validationErrors.map((error) => ({
          message: error.message,
          path: error.path,
        })),
      );
    }
  };

  const handleOnPreviousClick = () => {
    navigate(RoutesList.UPLOAD_PHOTO);
    scrollTop();
  };

  const buttonPrevious = {
    label: 'Previous',
    onClick: handleOnPreviousClick,
  };

  const buttonNext = {
    label: 'Next',
    onClick: handleOnNextClick,
  };

  const handleAddressChange = (address: addressResponseType) => {
    setErrorMessages([]);
    const formattedAddress = convertAddressToServiceLocation(address);
    updateServiceLocationDetails({
      ...serviceLocationDetails,
      ...formattedAddress,
    });
  };

  const pageTitle = isPhotoLocationStored
    ? 'Check if the location is correct'
    : 'Location of the issue';
  const subText = isPhotoLocationStored ? (
    <div className="text-bodyLg flex flex-col gap-4">
      <p>We used your photo to find where the issue is located.</p>
      <p>You can change where the issue is located using the map.</p>
    </div>
  ) : (
    <p className="text-bodyXl">Please select a location on the map.</p>
  );

  const addressTitle = isPhotoLocationStored
    ? 'Issue Location'
    : 'You have selected';

  const additionalLocationDetailsWithOnChange = {
    ...additionalLocationDetails,
    isLabelTextTitle: false,
    name: additionalLocationDetails.id,
    onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      updateIssuelocationMoreInfo(e.target.value);
    },
    value: issueLocationMoreInfo || '',
    showCounter: true,
    maxLength: 500,
    errorMessages: errorMessages.filter(
      (error) => error.path === additionalLocationDetails.id,
    ),
  };

  return (
    <MapTemplate
      id={errorPath}
      labelText={pageTitle}
      subText={subText}
      errorPath={errorPath}
      errorMessages={errorMessages}
      onChangeAddress={handleAddressChange}
      defaultLocation={defaultLocation}
      selectedAddress={selectedAddress}
      customAddressContent={
        <SelectedLocationTextContent title={addressTitle} />
      }
      additionalLocationDetailsTextArea={additionalLocationDetailsWithOnChange}
      enableTfLBoundaryCheck={true}
      navigationIfInsideTfLBoundary={navigationIfInsideTfLBoundary}
      assetType={assetType}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
    />
  );
};
